/**
 * Formula items and daily dishes linked to a catalogue dish (Product) read
 * their name, description, price and image through to the Product while their
 * own column is null (SFAPI-90: the Product's identity is shared). The API
 * serializes the resolved value, so a form prefilled from it must not send the
 * Product's values back, or they get pinned on the item and later Product
 * edits stop reaching it (SFRESTO-122).
 *
 * Return values: `null` = clear the own column (inherit from the Product),
 * `undefined` = leave the field out of the payload, anything else = override.
 */

export interface IdentityFieldContext<T> {
  /**
   * The Product's current value. Leave `undefined` when the Product is not
   * loaded (then only changed values are sent).
   */
  productValue?: T | null;
  /** The value the form was prefilled with (edit only). */
  initialValue?: T | null;
}

const normalizeText = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/** Same text as far as identity goes: Product titles are stored lowercased. */
export const isSameIdentityText = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => normalizeText(a) === normalizeText(b);

export function identityTextPayload(
  value: string,
  { productValue, initialValue }: IdentityFieldContext<string>
): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (productValue !== undefined) {
    return isSameIdentityText(trimmed, productValue) ? null : trimmed;
  }
  if (initialValue !== undefined && value === (initialValue ?? "")) {
    return undefined;
  }
  return trimmed;
}

/** `cents` is integer cents; 0 means "no own price". */
export function identityPricePayload(
  cents: number,
  { productValue, initialValue }: IdentityFieldContext<number>
): number | null | undefined {
  if (cents <= 0) return null;
  if (productValue !== undefined) {
    return cents === productValue ? null : cents;
  }
  if (initialValue !== undefined && cents === initialValue) return undefined;
  return cents;
}

export function identityImagePayload({
  uploadedIri,
  currentImageId,
  productImageId,
}: {
  /** IRI of an image uploaded in this form, if any. */
  uploadedIri?: string;
  /** Id of the image the item currently serializes (own or inherited). */
  currentImageId?: string;
  /** The Product's image id; `undefined` when the Product is not loaded. */
  productImageId?: string | null;
}): string | null | undefined {
  if (uploadedIri) return uploadedIri;
  if (currentImageId && productImageId && currentImageId === productImageId) {
    return null;
  }
  return undefined;
}

/** Form prefill for an optional override field: blank when it is inherited. */
export function inheritedTextForForm(
  effective: string | null | undefined,
  productValue: string | null | undefined
): string {
  if (
    productValue !== undefined &&
    isSameIdentityText(effective, productValue)
  ) {
    return "";
  }
  return effective ?? "";
}

/**
 * When the linked Product changes, a field that is empty or still shows the
 * previous Product's value follows the new Product; a typed value is kept.
 */
export function refillOnProductChange(
  current: string,
  previousProductValue: string | null | undefined,
  nextProductValue: string | null | undefined
): string {
  if (
    current.trim() === "" ||
    (previousProductValue != null &&
      isSameIdentityText(current, previousProductValue))
  ) {
    return nextProductValue ?? "";
  }
  return current;
}
